import { bezSierotek } from '@insolitum/ui';
import {
  Eye,
  EyeOff,
  UserPlus,
  AlertCircle,
  Check,
  Search,
  Building2,
  MapPin,
  Loader2,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  FOKUS_POLA_WEJSCIA,
  IKONA_SUKCESU,
  POLE_WEJSCIA,
  PoleWyboruWejscia,
  RAMKA_POLA_WEJSCIA,
  UkladWejscia,
} from '../../components/marka/UkladWejscia';
import {
  fetchCompanyByNip,
  validateNip,
  getVatStatusText,
  getVatStatusColor,
  type CompanyData,
} from '../../lib/vat-api';
import { useAuth } from '../../providers/AuthProvider';

export function RegisterPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // NIP and company data state
  const [nip, setNip] = useState('');
  const [nipError, setNipError] = useState<string | null>(null);
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const { signUp } = useAuth();

  // Handle NIP input change
  const handleNipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only digits and formatting characters
    const cleanValue = value.replace(/[^\d-]/g, '');
    setNip(cleanValue);
    setNipError(null);

    // Clear company data if NIP is changed
    if (companyData && cleanValue.replace(/\D/g, '') !== companyData.nip) {
      setCompanyData(null);
    }
  };

  // Fetch company data from VAT API
  const handleFetchCompany = useCallback(async () => {
    const cleanNip = nip.replace(/\D/g, '');

    if (!cleanNip) {
      setNipError(t('auth.nipRequired'));
      return;
    }

    if (!validateNip(cleanNip)) {
      setNipError(t('auth.invalidNip'));
      return;
    }

    setIsLoadingCompany(true);
    setNipError(null);

    try {
      const data = await fetchCompanyByNip(cleanNip);
      setCompanyData(data);

      // NIE podstawiamy nazwy firmy w pole „imię i nazwisko”. Robił to tu
      // auto-fill i wychodziło z tego konto OSOBY nazwane jak SPÓŁKA — wystarczyło
      // wpisać NIP przed swoim nazwiskiem. `users.full_name` jedzie potem wszędzie,
      // gdzie zwracamy się do człowieka: lista zespołu (partner zgłosił „nie wiem
      // skąd wziął się ten pracownik”, a to było jego własne konto podpisane nazwą
      // firmy), powitanie NEXUS-a i zwrot „Cześć {{name}}” w mailach.
      // Nazwa firmy ma swoje miejsce — `organizations.name` i metadane
      // `company_name` — i tam trafia z `companyData`.
    } catch (err) {
      if (err instanceof Error) {
        setNipError(err.message);
      } else {
        setNipError(t('auth.vatApiFailed'));
      }
      setCompanyData(null);
    } finally {
      setIsLoadingCompany(false);
    }
  }, [nip, t]);

  // Handle NIP blur - auto-fetch if valid
  const handleNipBlur = useCallback(() => {
    const cleanNip = nip.replace(/\D/g, '');
    if (cleanNip.length === 10 && validateNip(cleanNip) && !companyData) {
      handleFetchCompany();
    }
  }, [nip, companyData, handleFetchCompany]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate NIP
    const cleanNip = nip.replace(/\D/g, '');
    if (!cleanNip) {
      setError(t('auth.nipRequired'));
      return;
    }

    if (!validateNip(cleanNip)) {
      setError(t('auth.invalidNip'));
      return;
    }

    if (!companyData) {
      setError(t('auth.fetchCompanyFirst'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    if (password.length < 8) {
      setError(t('auth.passwordMinLength'));
      return;
    }

    if (!termsAccepted) {
      setError(t('auth.termsRequired'));
      return;
    }

    setIsLoading(true);

    try {
      await signUp(email, password, fullName, companyData);
      setSuccess(true);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t('errors.somethingWentWrong');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Stan sukcesu w tym samym układzie co formularz: znak i przełącznik
  // języka zostają na miejscu, zmienia się tylko treść pod nimi.
  if (success) {
    return (
      <UkladWejscia>
        <div className="card rounded-ids-2xl shadow-elev-4 p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 mb-4">
            <Check className={`w-8 h-8 ${IKONA_SUKCESU}`} />
          </div>
          <h1 className="ids-tytul-ekranu">{t('auth.checkEmail')}</h1>
          <p className="ids-tresc-ekranu mx-auto">
            {t('auth.confirmationSent')} <strong className="text-aurora-text">{email}</strong>
          </p>
          <Link to="/login" className="btn btn--primary btn--lg mt-8">
            {t('common.back')} {t('auth.signIn').toLowerCase()}
          </Link>
        </div>
      </UkladWejscia>
    );
  }

  return (
    <UkladWejscia>
      <div className="text-center">
        <h1 className="ids-tytul-ekranu">{t('auth.createAccount')}</h1>
        <p className="ids-tresc-ekranu mx-auto">{bezSierotek(t('auth.getStarted'))}</p>
      </div>

      {/* Register Form */}
      <div className="card rounded-ids-2xl shadow-elev-4 p-8 mt-8">
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-ids-md text-red-400 [[data-mode=light]_&]:text-red-800 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {bezSierotek(error)}
            </div>
          )}

          {/* NIP Field */}
          <div className="field">
            <label htmlFor="nip" className="field__label">
              {t('auth.nip')}{' '}
              <span className="text-red-400 [[data-mode=light]_&]:text-red-800">*</span>
            </label>
            <div className="relative">
              <input
                id="nip"
                type="text"
                value={nip}
                onChange={handleNipChange}
                onBlur={handleNipBlur}
                required
                maxLength={13}
                className={`input mono pr-28 ${FOKUS_POLA_WEJSCIA} ${
                  nipError
                    ? 'border-red-500'
                    : companyData
                      ? 'border-green-500 [[data-mode=light]_&:not(:focus)]:border-green-700'
                      : RAMKA_POLA_WEJSCIA
                }`}
                placeholder={t('auth.nipPlaceholder')}
              />
              <button
                type="button"
                onClick={handleFetchCompany}
                disabled={isLoadingCompany || !nip}
                className="btn btn--tonal btn--sm absolute right-1.5 top-1/2 -translate-y-1/2 [[data-mode=light]_&]:text-blue-700"
              >
                {isLoadingCompany ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                {t('auth.fetch')}
              </button>
            </div>
            {nipError && <p className="field__err">{bezSierotek(nipError)}</p>}
            <p className="field__hint">{t('auth.nipHint')}</p>
          </div>

          {/* Company Data Display */}
          {companyData && (
            <div className="p-4 bg-aurora-bg-subtle border border-aurora-border rounded-ids-lg space-y-3">
              <div className="flex items-center gap-2 text-green-400 [[data-mode=light]_&]:text-green-800 mb-3">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">{t('auth.companyFound')}</span>
              </div>

              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <Building2 className="w-4 h-4 text-aurora-subtle mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-aurora-subtle">{t('auth.companyName')}</p>
                    <p className="text-aurora-text font-medium">
                      {bezSierotek(companyData.companyName)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-aurora-subtle mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-aurora-subtle">{t('auth.address')}</p>
                    <p className="text-aurora-text">
                      {companyData.address && `${companyData.address}, `}
                      {companyData.postalCode} {companyData.city}
                      {companyData.county && `, pow. ${companyData.county}`}
                      {companyData.voivodeship && `, woj. ${companyData.voivodeship}`}
                    </p>
                  </div>
                </div>

                {companyData.regon && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-aurora-subtle">REGON:</span>
                    <span className="mono text-aurora-muted">{companyData.regon}</span>
                  </div>
                )}

                {companyData.krs && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-aurora-subtle">KRS:</span>
                    <span className="mono text-aurora-muted">{companyData.krs}</span>
                  </div>
                )}

                {/* Etykieta i pigułka nie łamią się wewnątrz („Status / VAT:”,
                    „Czynny podatnik / VAT” na telefonie do rundy 7c): obie
                    `whitespace-nowrap`, a gdy razem nie mieszczą się w ramce
                    („Czynny podatnik VAT” już przy 390 px), pigułka schodzi pod
                    etykietę (`flex-wrap`), zamiast wychodzić poza ramkę.
                    Bramka: `ekranyWejsciaWChromium` §12. */}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2">
                  <span className="whitespace-nowrap text-sm text-aurora-subtle">
                    {t('auth.vatStatus')}:
                  </span>
                  <span
                    className={`pill whitespace-nowrap ${
                      getVatStatusColor(companyData.vatStatus) === 'green'
                        ? 'pill--active [[data-mode=light]_&]:text-green-800'
                        : getVatStatusColor(companyData.vatStatus) === 'yellow'
                          ? 'pill--trial [[data-mode=light]_&]:text-amber-800'
                          : 'pill--error [[data-mode=light]_&]:text-red-800'
                    }`}
                  >
                    {companyData.vatStatus === 'active' && (
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                    )}
                    {companyData.vatStatus === 'not_registered' && (
                      <XCircle className="w-3 h-3 mr-1" />
                    )}
                    {bezSierotek(getVatStatusText(companyData.vatStatus))}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="field">
            <label htmlFor="fullName" className="field__label">
              {t('auth.fullName')}{' '}
              <span className="text-red-400 [[data-mode=light]_&]:text-red-800">*</span>
            </label>
            <input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              className={`input ${POLE_WEJSCIA}`}
              placeholder={t('auth.fullNamePlaceholder')}
            />
          </div>

          <div className="field">
            <label htmlFor="email" className="field__label">
              {t('auth.email')}{' '}
              <span className="text-red-400 [[data-mode=light]_&]:text-red-800">*</span>
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={`input ${POLE_WEJSCIA}`}
              placeholder={t('auth.emailPlaceholder')}
            />
          </div>

          <div className="field">
            <label htmlFor="password" className="field__label">
              {t('auth.password')}{' '}
              <span className="text-red-400 [[data-mode=light]_&]:text-red-800">*</span>
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={`input pr-12 ${POLE_WEJSCIA}`}
                placeholder={t('auth.passwordPlaceholder')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-aurora-subtle hover:text-aurora-text transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="field">
            <label htmlFor="confirmPassword" className="field__label">
              {t('auth.confirmPassword')}{' '}
              <span className="text-red-400 [[data-mode=light]_&]:text-red-800">*</span>
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className={`input ${POLE_WEJSCIA}`}
              placeholder={t('auth.confirmPasswordPlaceholder')}
            />
          </div>

          <div className="flex items-start">
            <PoleWyboruWejscia
              id="terms"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              required
              className="mt-0.5"
            />
            <label htmlFor="terms" className="ml-2 text-sm text-aurora-muted">
              {t('auth.termsAgreement')}{' '}
              <a href="/terms" className="text-aurora-brand hover:text-aurora-brand-hover">
                {t('auth.termsOfService')}
              </a>{' '}
              {t('auth.and')}{' '}
              <a href="/privacy" className="text-aurora-brand hover:text-aurora-brand-hover">
                {t('auth.privacyPolicy')}
              </a>
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading || !companyData}
            className="btn btn--primary btn--block"
          >
            {isLoading ? (
              <span className="spinner" />
            ) : (
              <>
                <UserPlus className="w-5 h-5" />
                {t('auth.createAccount')}
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-aurora-muted text-sm">
            {t('auth.haveAccount')}{' '}
            <Link
              to="/login"
              className="text-aurora-brand hover:text-aurora-brand-hover font-medium transition-colors"
            >
              {t('auth.signIn')}
            </Link>
          </p>
        </div>
      </div>
    </UkladWejscia>
  );
}
